import { Injectable } from '@angular/core';
import { forkJoin, Observable, ReplaySubject } from 'rxjs';
import { UiConfig, UiConfigsResource } from '../resources/ui-configs.resource';
import { UI_CONFIG_DEFAULTS } from '../utils/constants';
import { mergeConfigurations } from '../utils/helper-functions';
import { DeepPartial } from '../utils/types';
import { UserInfo, UserInfoService } from './user-info.service';

const USER_CONFIG_BROADCAST_CHANNEL_NAME = 'userConfig';
const ROLE_CONFIG_BROADCAST_CHANNEL_NAME = 'roleConfig';
const GLOBAL_CONFIG_BROADCAST_CHANNEL_NAME = 'globalConfig';

@Injectable({
  providedIn: 'root',
})
export class UiConfigService {
  private uiConfigSubject: ReplaySubject<UiConfig | null>;

  private globalConfigSubject: ReplaySubject<DeepPartial<UiConfig>>;

  private roleConfigSubject: ReplaySubject<DeepPartial<UiConfig> | null>;

  private userConfigSubject: ReplaySubject<DeepPartial<UiConfig> | null>;

  private userInfo: UserInfo | null;

  private globalConfig: DeepPartial<UiConfig> | null;

  private roleConfig: DeepPartial<UiConfig> | null;

  private userConfig: DeepPartial<UiConfig> | null;

  /**
   * Has no initial value (will try fetching UiConfig from server during service initialization).
   * The value of null is returned ONLY when service failed to load UiConfig from server
   */
  readonly uiConfig$: Observable<UiConfig | null>;

  readonly globalConfig$: Observable<DeepPartial<UiConfig>>;

  readonly roleConfig$: Observable<DeepPartial<UiConfig> | null>;

  readonly userConfig$: Observable<DeepPartial<UiConfig> | null>;

  private userConfigBroadcastChannel: BroadcastChannel;

  private roleConfigBroadcastChannel: BroadcastChannel;

  private globalConfigBroadcastChannel: BroadcastChannel;

  constructor(
    private uiConfigResourceService: UiConfigsResource,
    private userInfoService: UserInfoService,
  ) {
    this.uiConfigSubject = new ReplaySubject<UiConfig | null>(1);
    this.globalConfigSubject = new ReplaySubject<DeepPartial<UiConfig>>(1);
    this.roleConfigSubject = new ReplaySubject<DeepPartial<UiConfig> | null>(1);
    this.userConfigSubject = new ReplaySubject<DeepPartial<UiConfig> | null>(1);
    this.uiConfig$ = this.uiConfigSubject.asObservable();
    this.globalConfig$ = this.globalConfigSubject.asObservable();
    this.roleConfig$ = this.roleConfigSubject.asObservable();
    this.userConfig$ = this.userConfigSubject.asObservable();
    this.userInfo = null;
    this.globalConfig = null;
    this.roleConfig = null;
    this.userConfig = null;

    this.userConfigBroadcastChannel = new BroadcastChannel(USER_CONFIG_BROADCAST_CHANNEL_NAME);
    this.userConfigBroadcastChannel.addEventListener(
      'message',
      (message) => this.updateUserConfig(message.data, true),
    );
    this.roleConfigBroadcastChannel = new BroadcastChannel(ROLE_CONFIG_BROADCAST_CHANNEL_NAME);
    this.roleConfigBroadcastChannel.addEventListener(
      'message',
      (message) => this.updateRoleConfig(message.data, true),
    );
    this.globalConfigBroadcastChannel = new BroadcastChannel(GLOBAL_CONFIG_BROADCAST_CHANNEL_NAME);
    this.globalConfigBroadcastChannel.addEventListener(
      'message',
      (message) => this.updateGlobalConfig(message.data, true),
    );

    // Will fire when we receive UserInfo (after UserInfoService fetched UserInfo from server)
    userInfoService.userInfo$.subscribe((userInfo) => {
      this.userInfo = userInfo;
      this.reloadConfig();
    });
  }

  updateGlobalConfig(config: DeepPartial<UiConfig>, skipBroadcast?: boolean): void {
    this.globalConfig = config;
    this.globalConfigSubject.next(this.globalConfig);
    this.uiConfigSubject.next(this.recalculateConfig());
    if (!skipBroadcast) {
      this.globalConfigBroadcastChannel.postMessage(config);
    }
  }

  updateRoleConfig(config: DeepPartial<UiConfig>, skipBroadcast?: boolean): void {
    this.roleConfig = config;
    this.roleConfigSubject.next(this.roleConfig);
    this.uiConfigSubject.next(this.recalculateConfig());
    if (!skipBroadcast) {
      this.roleConfigBroadcastChannel.postMessage(config);
    }
  }

  updateUserConfig(config: DeepPartial<UiConfig>, skipBroadcast?: boolean): void {
    this.userConfig = config;
    this.userConfigSubject.next(this.userConfig);
    this.uiConfigSubject.next(this.recalculateConfig());
    if (!skipBroadcast) {
      this.userConfigBroadcastChannel.postMessage(config);
    }
  }

  reloadConfig(): void {
    if (this.userInfo != null) {
      const globalUiConfig$ = this.uiConfigResourceService.getGlobalUiConfig();
      const roleUiConfig$ = this.uiConfigResourceService.getRoleUiConfig(this.userInfo.roleId);
      const userUiConfig$ = this.uiConfigResourceService.getUserUiConfig(this.userInfo.login);
      forkJoin([globalUiConfig$, roleUiConfig$, userUiConfig$]).subscribe(
        (results) => {
          console.log('Loading UiConfig from server was successful');
          this.globalConfig = results[0];
          this.roleConfig = results[1];
          this.userConfig = results[2];
          this.globalConfigSubject.next(this.globalConfig);
          this.roleConfigSubject.next(this.roleConfig);
          this.userConfigSubject.next(this.userConfig);
          this.uiConfigSubject.next(this.recalculateConfig());
        },
        (error) => {
          console.log('Failed to load UiConfig from server', error);
          this.uiConfigSubject.next(null);
        },
      );
    } else {
      console.warn('UserInfo is missing, skipping loading UiConfig');
      this.uiConfigSubject.next(null);
    }
  }

  private recalculateConfig(): UiConfig | null {
    if (this.globalConfig != null && this.roleConfig != null && this.userConfig != null) {
      console.debug(
        'Recalculating contexts with UI configs',
        'defaults:', UI_CONFIG_DEFAULTS,
        'globalConfig', this.globalConfig,
        'roleConfig', this.roleConfig,
        'userConfig:', this.userConfig,
      );
      return mergeConfigurations(UI_CONFIG_DEFAULTS, this.globalConfig, this.roleConfig, this.userConfig);
    }
    console.debug(
      'Some configs are not initialized',
      'defaults:', UI_CONFIG_DEFAULTS,
      'globalConfig', this.globalConfig,
      'roleConfig', this.roleConfig,
      'userConfig:', this.userConfig,
    );
    return null;
  }
}
